/* eslint-disable no-magic-numbers -- 3D navigation math and rendering tolerances */
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DynamicDrawUsage,
  LineBasicMaterial,
  LineSegments,
  Object3D,
  Plane,
  Scene,
  Vector3,
} from '@technology/web3d';
import type { Api } from '@technology/web3d/dist/esnext/Api.js';
import { CrossSectionContourComputer } from './cross-section-contour-computer';
import type { ContourGroupResult } from './cross-section-contour-computer.types';

const LAYER_COUNT = 3;

const LAYER_COLORS = [
  new Color('#770000'),
  new Color('#bb0000'),
  new Color('#ff0000'),
];

const LAYER_RENDER_ORDERS = [999, 1000, 1001];

const _tmpNormal = new Vector3();
const _farPoint = new Vector3();
const _nearPoint = new Vector3();
const _lerpPoint = new Vector3();

/**
 * Renders depth-shaded cross-section contours: three layers of line segments
 * at evenly-spaced depths through the clipping slab, colored from dark red
 * (far) to bright red (near). Nearer contours naturally occlude farther ones
 * via depth testing, and where geometry changes across the slab thickness
 * the darker far-layer contours peek through.
 */
export class CrossSectionContourOverlay {
  private readonly _computer = new CrossSectionContourComputer();
  private readonly _materials: LineBasicMaterial[];
  private readonly _scene: Scene;
  private readonly _api: Api;
  private readonly _layerLines: LineSegments[][] = [[], [], []];
  private readonly _layerPlanes: Plane[] = [
    new Plane(),
    new Plane(),
    new Plane(),
  ];
  private _active = false;
  private _lastNearPlane: Plane | null = null;
  private _lastFarPlane: Plane | null = null;

  public modelRoots: Object3D[] = [];

  constructor(api: Api) {
    this._scene = api.scene;
    this._api = api;

    this._materials = LAYER_COLORS.map(
      (color) =>
        new LineBasicMaterial({
          color,
          depthTest: false,
          depthWrite: false,
          linewidth: 2,
        })
    );

    // Only keep the CPU-side geometry cache warm while the cross-section overlay is actually in use.
    api.renderingManager.addAnimationFrameListener(() => {
      if (!this._active) return;
      this._computer.cacheSceneGeometry(api.scene);
    });

    let pendingRefreshTimer = 0;
    const deferredRefresh = (): void => {
      if (pendingRefreshTimer) clearTimeout(pendingRefreshTimer);
      let remaining = 5;
      const tick = (): void => {
        if (!this._active || !this._lastNearPlane || !this._lastFarPlane)
          return;
        this._computer.cacheSceneGeometry(api.scene);
        this.update(this._lastNearPlane, this._lastFarPlane, this._api);
        remaining--;
        if (remaining > 0) {
          pendingRefreshTimer = window.setTimeout(tick, 100);
        } else {
          pendingRefreshTimer = 0;
        }
      };
      pendingRefreshTimer = window.setTimeout(tick, 100);
    };

    api.models.worldBoundingBox.subscribe(() => {
      if (this._active && this._lastNearPlane && this._lastFarPlane) {
        this._computer.cacheSceneGeometry(api.scene);
        this.update(this._lastNearPlane, this._lastFarPlane, this._api);
        deferredRefresh();
      }
    });
  }

  /**
   * @param nearPlane  The near clip plane (closest to camera, `_clipBoxPlanes[1]`).
   * @param farPlane   The far clip plane (at crosshair, `_clipBoxPlanes[0]`).
   */
  public update(nearPlane: Plane, farPlane: Plane, api: Api): void {
    this._active = true;
    this._lastNearPlane = nearPlane;
    this._lastFarPlane = farPlane;

    this._buildLayerPlanes(nearPlane, farPlane);

    const clippingPlanes = api.renderingManager.clippingPlanes;
    const roots = this.modelRoots.length > 0 ? this.modelRoots : undefined;

    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      const results = this._computer.compute(
        this._layerPlanes[layer],
        clippingPlanes,
        api,
        roots
      );
      this._syncLayerLines(layer, results);
    }

    api.renderingManager?.redraw();
  }

  /**
   * Build 3 planes evenly spaced between near and far, inset by a small
   * epsilon so they stay strictly between the clipping planes.
   *
   * Layer 0 = far (darkest), layer 2 = near (brightest).
   *
   * farPlane (clipBoxPlanes[0]) has normal -zAxis at alignmentPoint.
   * nearPlane (clipBoxPlanes[1]) has normal +zAxis at alignmentPoint - depth*zAxis.
   * We use the nearPlane's normal (pointing toward the camera) as the
   * consistent normal for all 3 layer planes.
   */
  private _buildLayerPlanes(nearPlane: Plane, farPlane: Plane): void {
    _tmpNormal.copy(nearPlane.normal);
    farPlane.coplanarPoint(_farPoint);
    nearPlane.coplanarPoint(_nearPoint);

    for (let i = 0; i < LAYER_COUNT; i++) {
      const t = (i + 0.5) / LAYER_COUNT;
      _lerpPoint.lerpVectors(_farPoint, _nearPoint, t);
      this._layerPlanes[i].setFromNormalAndCoplanarPoint(
        _tmpNormal,
        _lerpPoint
      );
    }
  }

  private _syncLayerLines(layer: number, results: ContourGroupResult[]): void {
    const lines = this._layerLines[layer];
    const material = this._materials[layer];
    const renderOrder = LAYER_RENDER_ORDERS[layer];

    while (lines.length > results.length) {
      const line = lines.pop()!;
      this._scene.remove(line);
      line.geometry.dispose();
    }

    for (let i = 0; i < results.length; i++) {
      const r = results[i];

      let line = lines[i];
      if (!line) {
        const geom = new BufferGeometry();
        const posAttr = new BufferAttribute(new Float32Array(8192 * 3), 3);
        posAttr.setUsage(DynamicDrawUsage);
        geom.setAttribute('position', posAttr);
        geom.setDrawRange(0, 0);

        line = new LineSegments(geom, material);
        line.frustumCulled = false;
        line.renderOrder = renderOrder;
        line.matrixAutoUpdate = false;
        line.matrixWorldAutoUpdate = false;
        lines.push(line);
        this._scene.add(line);
      }

      const vertexCount = r.segmentCount * 2;
      const geom = line.geometry;
      let posAttr = geom.getAttribute('position') as BufferAttribute;

      if (vertexCount > posAttr.count) {
        const newCount = Math.max(vertexCount, posAttr.count * 2);
        posAttr = new BufferAttribute(new Float32Array(newCount * 3), 3);
        posAttr.setUsage(DynamicDrawUsage);
        geom.setAttribute('position', posAttr);
      }

      (posAttr.array as Float32Array).set(
        r.segments.subarray(0, r.segmentCount * 6)
      );
      posAttr.needsUpdate = true;
      geom.setDrawRange(0, vertexCount);

      line.matrixWorld.copy(r.matrixWorld);
    }
  }

  public clear(): void {
    if (this._active) {
      for (const layerLines of this._layerLines) {
        for (const line of layerLines) {
          this._scene.remove(line);
          line.geometry.dispose();
        }
        layerLines.length = 0;
      }
      this._active = false;
      this._lastNearPlane = null;
      this._lastFarPlane = null;
      this._computer.clearCache();
    }
  }

  public dispose(): void {
    this.clear();
    for (const m of this._materials) m.dispose();
  }
}
